import { storage } from '@abyss/web/tools/storage';
import isEmpty from 'lodash/isEmpty';
import { useShallow } from 'zustand/react/shallow';

import { APIConstants } from '../../../api/src/services/Constants';
import {
  CareCategories,
  Constants,
  CoverageSNPTypes,
  CoverageTypesCodes,
  LINEOFBUSINESS_TRANSLATION,
  LOB,
  LineOfBusiness,
  MemberCoverageTypeToUICoverageTypeConversion,
  MemberType,
  PopulationType,
  ProviderType,
  ProviderTypeShortNames,
  ProviderTypes,
  RelationshipTypeCodes,
  ReverseCoverageTypesCodes,
  ReverseDentalCodes,
  ReverseVisionCodes,
} from '../common/Constants';
import { ConstantsLagoon } from '../common/ConstantsLagoon';
import { MemberInfo } from '../common/Portal/PortalMemberInfo';
import { getFeatureFlag } from '../common/Utils';
import {
  isCnsMember,
  isEniMember,
  isEniSsoMember,
  isEniUspMember,
  isLawwMember,
  isMnrMember,
  isSupportFinderMember,
  isUmrMember,
} from '../common/Utils/memberUtils/memberUtils';
import { removeLeadingZeroes } from '../common/Widget/SmartChoiceWidget/utils';
import { isLawwGuest } from '../frontends/GuestExperience/utils';
import { useCurrentMember } from '../hooks/useCurrentMember';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { StoreKeys } from '../hooks/useStore/state';
import { useStore } from '../hooks/useStore/useStore';
import { CustomPreferredPolicy } from '../models/Lagoon';
import { useGuestStore } from '../store/useGuestStore';
import { GuestState } from '../store/useGuestStore/guestStore';
import { DateTime } from '../utils/DateTime';
import { getMemberDataFromGuestData } from './guest.utils';

/**
 *
 * this will soon be @/deprecated
 * This function is deprecated. Please use `useCurrentMember` instead.
 *
 */
export const getCurrentMember = () => {
  const { guestData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
    }))
  );

  const isGuest = storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST);

  const memberInfo = useStore(StoreKeys.OBAPI_MEMBERS);

  if (guestData?.ndbLob) {
    return getMemberDataFromGuestData(guestData);
  }

  const guestUser = isGuest
    ? storage.session.get(Constants.STORAGE_KEYS.SESSION.GUEST_USER)
    : {};
  if (isGuest) {
    return guestUser;
  }

  const currentIndex =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.ACTIVE_MEMBER_INDEX) ||
    0;

  return memberInfo?.[currentIndex] ?? memberInfo?.[0];
};

export const getMemberType = (member: {
  lineOfBusiness: LOB;
  isIFPMember: boolean;
}) => {
  if (member.isIFPMember) {
    return MemberType.IFP;
  }
  if (member.lineOfBusiness === LOB.MNR) {
    return MemberType.MNR;
  }
  if (member.lineOfBusiness === LOB.ENI) {
    return MemberType.ENI;
  }
  if (member.lineOfBusiness === LOB.CNS) {
    return MemberType.CNS;
  }

  // fallback as ENI member
  return MemberType.ENI;
};

export const memberHasBHCoverage = (memberCoverages) =>
  memberCoverages?.find((cov) => cov === 'B') == 'B';

export const getCurrentMemberIsPreEffective = () =>
  getCurrentMember()?.isPreEffective;

export const getIsCurrentMemberSubscriber = () => {
  const currentMember = getCurrentMember();
  const memberRelationshipTypecode =
    currentMember?.demographics?.relationshipTypeCode?.code;
  return memberRelationshipTypecode === RelationshipTypeCodes.SUBSCRIBER;
};

export const isMnrCosmosMember = (coverageTypeCode) => {
  const currentMember = getCurrentMember();
  const { eligibilitySystemTypeCode, claimSystemTypeCode } =
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
      (coverage) => coverage.typeCode?.coverageTypeCode === coverageTypeCode
    ) || {};
  return eligibilitySystemTypeCode === 'MR' && claimSystemTypeCode === '02';
};

export const isLoggedInMember = () =>
  getCurrentMember() === getLoggedInMember();

export const getLoggedInMember = () => {
  const loggedInMemberIndex =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION.LOGGED_IN_MEMBER_INDEX
    ) || 0;
  const memberInfo = useStore(StoreKeys.OBAPI_MEMBERS);
  return memberInfo?.[loggedInMemberIndex];
};

export const getCoverageTypes = (currentMember) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.map(
    (coverage) => coverage?.typeCode?.coverageTypeCode
  );

export const filterCoverageTypesForStandaloneDental = (coverageTypes) =>
  coverageTypes.filter((coverage) => ReverseDentalCodes.includes(coverage));

export const isDsnpMember = (currentMember) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType
    ?.map((coverage) => coverage?.snpType)
    .find((snpType) => snpType === CoverageSNPTypes.DSNP) ===
  CoverageSNPTypes.DSNP;

export const getSitusState = (currentMember, lineOfBusiness) => {
  const situsState =
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.[0]
      ?.situsState;

  const lob = currentMember?.lineOfBusiness ?? lineOfBusiness;

  return isCNSMember(lob) ? situsState : '';
};

export const isGroupMNRMember = (currentMember) => {
  const fundingArrangement =
    isMnrMember(currentMember?.lineOfBusiness) &&
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType
      ?.map((coverage) => coverage.fundingArrangement)
      .find((group) => group === 'G');
  return !!fundingArrangement;
};

export const isIndividualMNRMember = (currentMember) => {
  const fundingIndividual =
    isMnrMember(currentMember?.lineOfBusiness) &&
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType
      ?.map((coverage) => coverage.fundingArrangement)
      .find((group) => group === 'I');
  return !!fundingIndividual;
};

export const getMemberCoverageTypeSitusState = (currentMember): string => {
  const coverageType =
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.[0];
  return coverageType?.situsState || '';
};
export const getActiveCoverages = (currentMember) => {
  const todayDate = DateTime.getCurrentDateTimeWithCentralOffset()
    .toISOString()
    .slice(0, 10);

  return currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType
    ?.map((coverageType) =>
      coverageType?.coverageEffDateRange?.endDate >= todayDate &&
      coverageType?.coverageEffDateRange?.startDate <= todayDate
        ? coverageType.typeCode.coverageTypeCode
        : null
    )
    .filter((coverageTypeCode) => coverageTypeCode !== null);
};

export const getCoverageEffDateRange = (
  currentMember: MemberInfo,
  coverageType: string
) => {
  const coverageData =
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.filter(
      (covType) => covType?.typeCode?.coverageTypeCode === coverageType
    );
  const startDate = coverageData?.[0]?.coverageEffDateRange?.startDate;
  const endDate = coverageData?.[0]?.coverageEffDateRange?.endDate;
  return { startDate, endDate };
};

export const getMarketTypes = (currentMember) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.map(
    (coverage) => coverage.marketType
  );
export const getProductCode = (currentMember) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.map(
    (coverage) => coverage.productCode
  );
export const getProductCodeForCoverageType = (
  currentMember,
  coverageTypeCode
) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage) => coverage.typeCode?.coverageTypeCode === coverageTypeCode
  )?.productCode;

export const getPolicyNumber = (currentMember, coverageTypeCode) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage) => coverage.typeCode?.coverageTypeCode === coverageTypeCode
  )?.policyNumber;

export const getReciprocityIdForGuest = (currentMember, coverageTypeCode) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage) => coverage.typeCode?.coverageTypeCode === coverageTypeCode
  )?.reciprocityId;

export const isTypeCodeMatchingCoverageType = (
  coverageType: string | undefined,
  memberCoverageTypeCode: string
) => {
  if (coverageType === 'V') {
    return ReverseVisionCodes.includes(memberCoverageTypeCode);
  }
  if (coverageType === 'D') {
    return ReverseDentalCodes.includes(memberCoverageTypeCode);
  }
  return memberCoverageTypeCode === (coverageType ?? 'M');
};

export const isPlanVariationCodeRequired = (
  lob,
  population,
  portalName,
  isIFPEarlyPay,
  isGuestUser,
  isStandaloneDentalUser
) => {
  const isUsp = isEniUspMember(lob, population);
  const isMnr = isMnrMember(lob);
  const isUmrPopulation = isUmrMember(portalName);
  const isLaww = isLawwMember(portalName);
  const isSupportFinder = isSupportFinderMember(portalName);
  const isEniSsoPopulation = isEniSsoMember(portalName);
  return !(
    isStandaloneDentalUser ||
    isUsp ||
    isMnr ||
    isUmrPopulation ||
    isLaww ||
    isSupportFinder ||
    isEniSsoPopulation ||
    isIFPEarlyPay ||
    isGuestUser
  );
};

export const getIsPolicyIDRequired = (
  currentMember: any,
  isGuestUser = false
): boolean => currentMember?.isIFPEarlyPay !== true && !isGuestUser;

export const getPlanVariationCode = (currentMember, coverageTypeCode) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage) =>
      isTypeCodeMatchingCoverageType(
        coverageTypeCode,
        coverage.typeCode?.coverageTypeCode
      )
  )?.planVariationCode;

export const getClaimOrEligibilitySystemTypeCode = (
  currentMember,
  coverageTypeCode
) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage) =>
      isTypeCodeMatchingCoverageType(
        coverageTypeCode,
        coverage.typeCode?.coverageTypeCode
      )
  ) || {};

export const getReportingCode = (currentMember, coverageTypeCode) =>
  currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage) => coverage.typeCode?.coverageTypeCode === coverageTypeCode
  )?.reportingCode;

export const getReciprocityId = (
  filterParams,
  currentMember,
  coverageType,
  featureFlags
) =>
  filterParams.planTypeFilterValue
    ? filterParams.planTypeFilterValue
    : getNetworkIdsForPES(currentMember, coverageType, featureFlags);

export const getCurrentMemberPCPInfo = (
  dependentSeqNbr,
  primaryCare,
  isPreEffective = false
) => {
  const primaryCareFuturePcpInfo = primaryCare?.find(
    ({ depSeqNbr, pcpInfo }) => depSeqNbr === dependentSeqNbr && !pcpInfo.hasPCP
  );
  if (isPreEffective && primaryCareFuturePcpInfo) {
    return primaryCareFuturePcpInfo?.pcpInfo.futurePCP;
  } else {
    return primaryCare?.find(
      ({ depSeqNbr, pcpInfo }) =>
        pcpInfo.hasPCP &&
        pcpInfo.isCoverageActive &&
        depSeqNbr === dependentSeqNbr
    )?.pcpInfo;
  }
};

export const getCurrentMemberPCPorFuturePCPInfo = (
  dependentSeqNbr,
  primaryCare
) =>
  primaryCare?.find(
    ({ depSeqNbr, pcpInfo }) =>
      ((pcpInfo.hasPCP && pcpInfo.isCoverageActive) ||
        !!pcpInfo.futurePCP?.pcpID) &&
      depSeqNbr === dependentSeqNbr
  )?.pcpInfo;

export const getCurrentPlanYear = () =>
  String(DateTime.getCurrentDateTimeWithCentralOffset().getUTCFullYear());

export const getMemberEffectivePlanYear = (memberEffectiveDate: Date) =>
  String(memberEffectiveDate.getFullYear());

export const getMemberByDependentSeqNbr = (dependentSeqNbr) => {
  const memberInfo = useStore(StoreKeys.OBAPI_MEMBERS);
  const reqMember = memberInfo.filter(
    (member) => member.demographics.dependentSeqNbr === dependentSeqNbr
  );
  return reqMember[0];
};

export const getMemberPlanYear = (
  isPreEffective: boolean,
  currentMemberEffectiveDate: string
) => {
  const memberEffectiveYear = getMemberEffectivePlanYear(
    new Date(currentMemberEffectiveDate?.replace(/-/g, '/'))
  );
  const currentYear = getCurrentPlanYear();
  return isPreEffective ? memberEffectiveYear : currentYear;
};

export const getPreEffectiveDateforNetwork = (coverageType, isPreEffective) => {
  const { startDate = '' } = coverageType?.coverageEffDateRange || {};
  let preEffectiveDate = '';
  if (startDate && isPreEffective) {
    // convert 2024-10-01 to 20241001
    preEffectiveDate = `:${startDate.replaceAll('-', '')}`;
  }
  return preEffectiveDate;
};

const getBhNetworkId = (
  isMnrUser: boolean,
  isCnsUser: boolean,
  isIFPMember: boolean,
  reciprocityId: string,
  preEffectiveDate: string
): string => {
  if (isMnrUser) {
    return `bh_${reciprocityId}${preEffectiveDate}`;
  }
  if (isCnsUser || isIFPMember) {
    return `bh_${reciprocityId}`;
  }
  return `${Constants.NETWORK_COVERAGE_CODE.BEHAVIORAL_B}${preEffectiveDate}`;
};

const getMedicalNetworkId = (
  currentMember,
  coverageType,
  isCnSDsnpMember: boolean,
  isIFPMember: boolean,
  isMnrUser: boolean,
  isCnsUser: boolean,
  isEniGuestUser: boolean,
  isGuestOxfordMember: boolean,
  mdReciprocityId: string,
  excludeNatAnc: boolean,
  preEffectiveDate: string
): string => {
  const isSierra = currentMember?.population === PopulationType.SIERRA;
  const excludeNatAncCommercial = excludeNatAnc
    ? ''
    : `%3B${Constants.NETWORK_COVERAGE_CODE.MEDICAL}${preEffectiveDate}`;
  const medicalCov = isSierra
    ? `mdsierra_${currentMember?.reciprocityId}${preEffectiveDate}%3B${Constants.NETWORK_COVERAGE_CODE.SIERRA_MEDICAL}${preEffectiveDate}`
    : `md_${currentMember?.reciprocityId}${preEffectiveDate}${excludeNatAncCommercial}`;

  const preText = isGuestOxfordMember
    ? 'mdox'
    : isEniGuestUser
    ? 'md'
    : coverageType?.snpType === CoverageSNPTypes.DSNP || isCnSDsnpMember
    ? 'mddsnp'
    : isCnsUser
    ? 'mdcns'
    : isIFPMember
    ? 'mdifp'
    : 'mdcosmos';

  if (isMnrUser || isCnsUser || isIFPMember || isEniGuestUser) {
    return mdReciprocityId
      ? mdReciprocityId
          .split(',')
          .map((val) => `${preText}_${val}${preEffectiveDate}`)
          .join('%3B')
      : '';
  }

  return medicalCov;
};

const getCoverageTypeCodes = (
  currentMember,
  coverageTypeCodeMap,
  isPreEffective,
  excludeNatAnc
) => {
  const {
    lineOfBusiness = '',
    reciprocityId: mdReciprocityId = '',
    isCnSDsnpMember,
    isIFPMember,
  } = currentMember || {};
  const isGuest = storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST);
  const isMnrUser = isMnrMember(lineOfBusiness);
  const isCnsUser = isCnsMember(lineOfBusiness);
  const isEniGuestUser = isGuest && isEniMember(lineOfBusiness);
  const isGuestOxfordMember =
    isEniGuestUser && currentMember.membershipCategory === 'OXFORD';
  const todayDate = DateTime.getCurrentDateTimeWithCentralOffset()
    .toISOString()
    .slice(0, 10);

  return currentMember?.eligibility
    .flatMap((coverage) => coverage?.memberHealthCoverage?.coverageType)
    .filter(
      (coverageType) =>
        coverageTypeCodeMap[coverageType?.typeCode?.coverageTypeCode] !==
        undefined
    )
    .filter((coverageType) =>
      // if it's a pre-effective plan, do not filter out the coverages
      isPreEffective
        ? true
        : coverageType?.coverageEffDateRange?.endDate >= todayDate &&
          coverageType?.coverageEffDateRange?.startDate <= todayDate
    )
    .map((coverageType) => {
      const preEffectiveDate = getPreEffectiveDateforNetwork(
        coverageType,
        isPreEffective
      );
      const { coverageTypeCode = '', reciprocityId = '' } =
        coverageType?.typeCode || {};
      // append pre-effective date to all the network ids
      if (
        coverageTypeCode ===
        ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_HEALTH]
      ) {
        return getBhNetworkId(
          isMnrUser,
          isCnsUser,
          isIFPMember,
          reciprocityId,
          preEffectiveDate
        );
      }
      if (coverageTypeCode === ReverseCoverageTypesCodes.MEDICAL) {
        return getMedicalNetworkId(
          currentMember,
          coverageType,
          isCnSDsnpMember,
          isIFPMember,
          isMnrUser,
          isCnsUser,
          isEniGuestUser,
          isGuestOxfordMember,
          mdReciprocityId,
          excludeNatAnc,
          preEffectiveDate
        );
      }
      return `${coverageTypeCodeMap[coverageTypeCode]}${preEffectiveDate}`;
    });
};

export const getMemberCoverage = (
  currentMember: any,
  featureFlags: { key: string; active: boolean }[],
  excludeNatAnc: boolean,
  isEligibleForStandaloneDental: boolean = false
) => {
  const isSuppressPreEffectiveFF: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.IS_SUPPRESS_PREEFFECTIVE
  );
  const isCurrentMemberPreEffective = currentMember?.isPreEffective === true;
  const isPreEffective =
    !isSuppressPreEffectiveFF && isCurrentMemberPreEffective;

  const visionChipEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.VISION_CHIP_ENABLED
  );
  const dentalChipEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DENTAL_CHIP_ENABLED
  );
  const visionNetworkId =
    currentMember?.networks?.filter(
      (coverage) =>
        ReverseCoverageTypesCodes[coverage?.type] ===
          ReverseCoverageTypesCodes.VISION ||
        ReverseCoverageTypesCodes[coverage?.type] ===
          ReverseCoverageTypesCodes.VISIONEMBEDDED
    )[0]?.value || [];

  const dentalNetworkId =
    currentMember?.networks?.filter(
      (coverage) =>
        ReverseCoverageTypesCodes[coverage?.type] ===
          ReverseCoverageTypesCodes.DENTAL ||
        ReverseCoverageTypesCodes[coverage?.type] ===
          ReverseCoverageTypesCodes.DENTALEMBEDDED
    )[0]?.value || [];

  const coverageTypeCodeMap = {
    [ReverseCoverageTypesCodes.MEDICAL]: '',
    [ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_HEALTH]]: '',
    [ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_CARVE_OUT]]: `bh_${
      currentMember?.bhReciprocityId || ''
    }`,
    [ReverseCoverageTypesCodes.VISION]:
      visionChipEnabled &&
      !isEligibleForStandaloneDental &&
      visionNetworkId.length > 0
        ? visionNetworkId.map((id) => `vn_${id}`).join('%3B')
        : '',
    [ReverseCoverageTypesCodes.DENTAL]:
      dentalChipEnabled && dentalNetworkId.length > 0
        ? dentalNetworkId.map((id) => `dt_${id}`).join('%3B')
        : '',
    [ReverseCoverageTypesCodes.VISIONEMBEDDED]:
      visionChipEnabled &&
      !isEligibleForStandaloneDental &&
      visionNetworkId.length > 0
        ? visionNetworkId.map((id) => `vn_${id}`).join('%3B')
        : '',
    [ReverseCoverageTypesCodes.DENTALEMBEDDED]:
      dentalChipEnabled && dentalNetworkId.length > 0
        ? dentalNetworkId.map((id) => `dt_${id}`).join('%3B')
        : '',
  };

  const coverageTypeCodes = getCoverageTypeCodes(
    currentMember,
    coverageTypeCodeMap,
    isPreEffective,
    excludeNatAnc
  );
  return coverageTypeCodes
    .filter(
      (networkType: string) =>
        networkType !== 'undefined' &&
        networkType !== '' &&
        networkType !== null
    )
    ?.join('%3B');
};

//Utility function to get the BH coverage type codes for getNetworkIdsForPES
const getNetworkIdsForBh = (currentMember: any, featureFlags) => {
  const isSuppressPreEffectiveFF: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.IS_SUPPRESS_PREEFFECTIVE
  );
  const todayDate = DateTime.getCurrentDateTimeWithCentralOffset()
    .toISOString()
    .slice(0, 10);
  const coverageTypeCodeMap = {
    [ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_HEALTH]]:
      Constants.NETWORK_COVERAGE_CODE_PES.BEHAVIORAL_B,
    [ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_CARVE_OUT]]:
      currentMember.bhReciprocityId,
  };
  const { lineOfBusiness } = currentMember;
  const isCurrentMemberPreEffective = currentMember?.isPreEffective === true;
  const isPreEffective =
    !isSuppressPreEffectiveFF && isCurrentMemberPreEffective;
  const coverageTypeCodes = currentMember?.eligibility
    .flatMap((coverage) => coverage?.memberHealthCoverage?.coverageType)
    .filter((covType) =>
      isPreEffective
        ? true
        : covType?.coverageEffDateRange?.endDate >= todayDate &&
          covType?.coverageEffDateRange?.startDate <= todayDate
    )
    .map((covType) => {
      const isLoBMNR = lineOfBusiness === LOB.MNR;
      const isLobCNS = lineOfBusiness === LOB.CNS;
      const isLobIFP = lineOfBusiness === LOB.IFP;
      const isLobENI = lineOfBusiness === LOB.ENI;

      const isGuestUser = storage.session.get(
        Constants.STORAGE_KEYS.SESSION.IS_GUEST
      );
      const isCovTypeBh =
        covType?.typeCode?.coverageTypeCode ===
        ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_HEALTH];
      const covTypeReciprocityId = covType?.typeCode?.reciprocityId;
      const shouldReturnReciprocityId =
        (isLoBMNR ||
          isLobCNS ||
          isLobIFP ||
          ((isLawwGuest() || isGuestUser) && isLobENI)) &&
        isCovTypeBh;
      /** DE963347 fix - in getNetworkIdsForBh, if coverageType is B and LOB is MNR or CNS, then
       * return reciprocityId from coverageTypeCode of BH coverage. else default is coverageTypeCodeMap that's constant.
       */
      return shouldReturnReciprocityId
        ? covTypeReciprocityId
        : coverageTypeCodeMap[covType?.typeCode?.coverageTypeCode] || '';
    });
  return coverageTypeCodes
    .filter((networkType: string) => networkType)
    ?.join(',');
};

//utility function to get network id for dental codes for getNetworkIdsForPES
const getNetworkIdsForDental = (currentMember) => {
  const networks =
    currentMember?.networks?.filter((coverage) =>
      ReverseDentalCodes.includes(ReverseCoverageTypesCodes[coverage?.type])
    ) || [];
  const networksReciprocityId = networks[0]?.value?.join(',') || '';
  const currentMemberReciprocityId = currentMember?.reciprocityId;
  return networksReciprocityId || currentMemberReciprocityId;
};

//utility function to get network id for vision codes for getNetworkIdsForPES
const getNetworkIdsForVision = (currentMember, featureFlags) => {
  const visionChipEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.VISION_CHIP_ENABLED
  );
  if (visionChipEnabled) {
    const networks =
      currentMember?.networks?.filter((coverage) =>
        ReverseVisionCodes.includes(ReverseCoverageTypesCodes[coverage?.type])
      ) || [];
    return networks[0]?.value?.join(',') || '' || currentMember?.reciprocityId;
  }
  return '0';
};

type MockReciprocityConfig = {
  selectedCoverageType?: string;
  mockedReciprocityId?: string;
  isEnabled?: boolean;
};

const getMockedReciprocityId = (coverageType: string) => {
  const mockReciprocityConfig = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.MOCK_RECIPROCITY_CONFIG,
    {}
  ) as MockReciprocityConfig;

  if (
    !mockReciprocityConfig?.isEnabled ||
    !mockReciprocityConfig?.mockedReciprocityId ||
    !mockReciprocityConfig?.selectedCoverageType
  ) {
    return null;
  }

  const normalizedCoverageType =
    MemberCoverageTypeToUICoverageTypeConversion[coverageType] || coverageType;

  if (normalizedCoverageType !== mockReciprocityConfig.selectedCoverageType) {
    return null;
  }

  return mockReciprocityConfig;
};

export const getNetworkIdsForPES = (
  currentMember: any,
  coverageType: string,
  featureFlags: { key: string; active: boolean }[]
) => {
  const mockedReciprocityId =
    getMockedReciprocityId(coverageType)?.mockedReciprocityId;
  const mockedCoverageType =
    getMockedReciprocityId(coverageType)?.selectedCoverageType;

  if (mockedReciprocityId && mockedCoverageType === coverageType) {
    return mockedReciprocityId;
  }

  if (coverageType === 'B') {
    return getNetworkIdsForBh(currentMember, featureFlags);
  }

  if (ReverseDentalCodes.includes(coverageType)) {
    return getNetworkIdsForDental(currentMember);
  }

  if (ReverseVisionCodes.includes(coverageType)) {
    return getNetworkIdsForVision(currentMember, featureFlags);
  }

  return currentMember?.reciprocityId;
};

export const getOnlineRetailers = (isOnlineProviders: any) => {
  if (typeof isOnlineProviders === 'boolean') return isOnlineProviders;
  if (isOnlineProviders === null) return null;
  if (isOnlineProviders === 'Y') return true;
  if (isOnlineProviders === 'N') return false;
  if (isOnlineProviders?.toLowerCase() === 'blank') return null;
  return undefined;
};

export const isMemberEAPEligible = () =>
  storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_BH_EAP_ELIGIBLE) ||
  false;

export const getCustomPreferredPolicyData = (
  customPreferredPolicies: CustomPreferredPolicy[],
  policyId: string,
  reciprocityId: string,
  situsState: string
): CustomPreferredPolicy | undefined =>
  customPreferredPolicies?.find((cpp) => {
    // split to get true ReciprocityId - for Guest it may be formatted as "10169,NatAncCommerical" (or other forms).
    const reciprocityIds =
      reciprocityId?.split(',').map((id) => id.trim()) ?? [];

    if (cpp.productName === 'OBH CUSTOM NETWORK') {
      return (
        reciprocityId === cpp.reciprocityId ||
        reciprocityIds.includes(cpp.reciprocityId)
      );
    }

    let isMatch = true;

    // If set, situsState must match member's situsState
    if (
      cpp.situsState &&
      cpp.situsState.toLowerCase() !== situsState?.toLowerCase()
    ) {
      isMatch = false;
    }

    // If set, policyId must match member's policyId
    if (
      cpp.policyId &&
      removeLeadingZeroes(cpp.policyId) !== removeLeadingZeroes(policyId)
    ) {
      isMatch = false;
    }

    // reciprocityid must match (or be included in the list of) member's reciprocityIds
    if (
      cpp.reciprocityId &&
      cpp.reciprocityId !== reciprocityId &&
      !reciprocityIds.includes(cpp.reciprocityId)
    ) {
      isMatch = false;
    }

    return isMatch;
  });

export const getIsCustomPreferredPolicy = (
  customPreferredPolicies: CustomPreferredPolicy[],
  policyId: string,
  reciprocityId: string,
  situsState: string
): boolean =>
  !!getCustomPreferredPolicyData(
    customPreferredPolicies,
    policyId,
    reciprocityId,
    situsState
  );

export const useCustomPolicy = (
  selectedMember,
  tableFunction1,
  lob
): {
  isCustomPreferredPolicy: boolean;
  customPreferredPolicyData: CustomPreferredPolicy | undefined;
} => {
  const data =
    tableFunction1 != null
      ? { ['custom-preferred-policies']: tableFunction1() }
      : null;
  const res = data != null ? data['custom-preferred-policies'] : [];

  const currentMember = selectedMember ?? {
    policyNumber: '',
    reciprocityId: '',
  };

  const situsState = getSitusState(currentMember, lob);

  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    res,
    currentMember?.policyNumber ?? '',
    currentMember?.reciprocityId ?? '',
    situsState
  );

  const customPreferredPolicyData = getCustomPreferredPolicyData(
    res,
    currentMember?.policyNumber ?? '',
    currentMember?.reciprocityId ?? '',
    situsState
  );

  return { isCustomPreferredPolicy, customPreferredPolicyData };
};

export const getMemberShipCategory = (
  currentMember: any,
  featureFlags: { key: string; active: boolean }[]
) => {
  const uspToggleFlag = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE
  );
  const memCatergory = currentMember?.membershipCategory || '';
  return uspToggleFlag ? memCatergory : null;
};

export const getIsOxfordAndUSP = (
  currentMember,
  coverageType: string,
  featureFlags: { key: string; active: boolean }[]
) => {
  const { claimSystemTypeCode, eligibilitySystemTypeCode } =
    getClaimOrEligibilitySystemTypeCode(currentMember, coverageType);
  const membershipCategory = getMemberShipCategory(currentMember, featureFlags);

  let isUSPMember: boolean = false;
  let isOXMember: boolean = false;
  if (
    (eligibilitySystemTypeCode?.toUpperCase() === 'CR' &&
      claimSystemTypeCode?.toUpperCase() === 'CR') ||
    [PopulationType.USP, PopulationType.ENI_NICE].includes(
      currentMember?.population || ''
    )
  ) {
    isUSPMember = true;

    if (membershipCategory?.toUpperCase() === 'OXFORD') {
      isOXMember = true;
    }
  }

  return {
    isUSPMember,
    isOXMember,
  };
};

export const isNotMNRMember = (population) =>
  population !== PopulationType.COSMOS;

export const isMNRMember = (lineOfBusiness) => lineOfBusiness === LOB.MNR;
export const isCNSMember = (lineOfBusiness) => lineOfBusiness === LOB.CNS;

export const isMnrTieredPlan = (tierBenefits, rulesPackageKey) => {
  const hpbpCodes = tierBenefits?.map(({ hpbpCode }) => hpbpCode);
  return !!hpbpCodes?.includes(rulesPackageKey);
};

export const validateMnrTieredBenefitEligibility = (
  currentMember,
  tierBenefits,
  providerType
): boolean => {
  const { rulesPackageKey = '' } = currentMember || {};
  return !!tierBenefits?.find(
    ({ hpbpCode, providerType: eligibleProviderType }) =>
      hpbpCode === rulesPackageKey &&
      eligibleProviderType
        ?.toLowerCase()
        ?.replaceAll('_', '')
        ?.split(',')
        ?.includes(
          providerType?.toLowerCase()?.replaceAll(' ', '')?.replaceAll('_', '')
        )
  );
};

export const getRulesPackageKey = (
  currentMember,
  tierBenefits,
  providerType,
  uspTierOneFlag,
  enableTier1FromMemberRPK = false
) => {
  let rulesPackageKey =
    uspTierOneFlag || enableTier1FromMemberRPK
      ? currentMember?.rulesPackageKey || ''
      : null;
  if (isMNRMember(currentMember?.lineOfBusiness)) {
    rulesPackageKey = validateMnrTieredBenefitEligibility(
      currentMember,
      tierBenefits,
      providerType
    )
      ? rulesPackageKey
      : null;
  }
  return rulesPackageKey;
};

export const isTier1Plan = (
  enableTier1RulesPackageFF: boolean,
  memberRulesPackageKey: string,
  isTiredPlanVal: boolean | undefined
): boolean => {
  const shouldEnableTier1 =
    enableTier1RulesPackageFF && !isEmpty(memberRulesPackageKey);
  if (shouldEnableTier1) {
    return true;
  }
  if (isTiredPlanVal !== undefined) {
    return isTiredPlanVal;
  }
  return false;
};

export const modifyRulesPackageKey = (memberInfo, tierBenefits) => {
  const currentMember = memberInfo?.[0] || {};
  const { lineOfBusiness } = currentMember;

  if (isMNRMember(lineOfBusiness)) {
    const { contractNumber, pbpNumber, segmentId } =
      currentMember?.eligibility?.[0]?.memberHealthCoverage
        ?.coverageType?.[0] || {};
    const rulesPackageKey = `${contractNumber}-${pbpNumber}-${segmentId}`;
    return memberInfo?.map((member) => {
      member.rulesPackageKey = isMnrTieredPlan(tierBenefits, rulesPackageKey)
        ? rulesPackageKey
        : '';
      return member;
    });
  }
  return memberInfo;
};

export const isOHCNSMember = (OHpolicyIds: string[] = []) => {
  const { policyNumber } = getCurrentMember();
  return policyNumber != null && OHpolicyIds.includes(policyNumber);
};

/**
 * Returns true if the current member's plan (contractNumber-pbpNumber-segmentId)
 * is in the provided list of referral-suppressed plan IDs (e.g. ["H2247-005-000"]).
 * Used to suppress referral required messaging for excluded M&R plans (US10295740).
 */
export const isMemberOnExcludedReferralPlan = (
  currentMember: any,
  suppressedPlanIds: string[]
): boolean => {
  if (!suppressedPlanIds?.length) return false;
  const coverageTypes =
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType || [];
  const coverageType =
    coverageTypes.find(
      (coverage: any) => coverage?.typeCode?.coverageTypeCode === 'M'
    ) || coverageTypes[0];
  const { contractNumber, pbpNumber, segmentId } = coverageType || {};

  if (!contractNumber || !pbpNumber || !segmentId) return false;

  const normalizePlanId = (planId: string): string =>
    (planId || '')
      .trim()
      .replace(/^['\"]+|['\"]+$/g, '')
      .replace(/[\u2010-\u2015]/g, '-')
      .toUpperCase();

  const planKey = normalizePlanId(
    `${contractNumber}-${pbpNumber}-${segmentId}`
  );

  return suppressedPlanIds.some((id) => normalizePlanId(id) === planKey);
};

export const getProviderTypeShortName = (
  providerType: ProviderTypes
): ProviderTypeShortNames | undefined => {
  switch (providerType?.toLowerCase()) {
    case ProviderTypes.ORGANIZATION.toLowerCase():
      return ProviderTypeShortNames.ORGANIZATION;
    case ProviderTypes.PRACTITIONER.toLowerCase():
      return ProviderTypeShortNames.PRACTITIONER;
    case ProviderTypes.GROUP.toLowerCase():
      return ProviderTypeShortNames.PROVIDER_GROUP;
    case ProviderTypes.MEDICAL_GROUP.toLowerCase():
      return ProviderTypeShortNames.MEDICAL_GROUP;
    case ProviderTypes.MEDICALGROUP.toLowerCase():
      return ProviderTypeShortNames.MEDICAL_GROUP;
    default:
      return undefined;
  }
};

export const getAdditionalAttributes = (
  additionalAttributes,
  currentMember,
  coverageType,
  providerType,
  apiName
) => {
  const { policyNumber } = currentMember;
  const matchingAttributes = additionalAttributes?.filter(
    (attribute) =>
      (attribute.policyIds?.includes('ALL') ||
        attribute.policyIds?.includes(policyNumber)) &&
      attribute.coverage?.includes(coverageType) &&
      attribute.providerType?.includes(
        getProviderTypeShortName(providerType?.toLowerCase())
      ) &&
      attribute.gqlApi?.includes(apiName) &&
      attribute.active
  );
  return matchingAttributes?.map((item) => item?.dntAttributeName);
};

export const isDentalNetworkIdNotAvailable = (
  selectedChipValue,
  currentMember,
  coverageType,
  showDentalChip
): boolean => {
  const dentalNetworkId =
    currentMember?.networks?.find(
      (coverage) =>
        ReverseCoverageTypesCodes[coverage?.type] ===
          ReverseCoverageTypesCodes.DENTAL ||
        ReverseCoverageTypesCodes[coverage?.type] ===
          ReverseCoverageTypesCodes.DENTALEMBEDDED
    )?.value?.[0] || '';

  return (
    coverageType === ReverseCoverageTypesCodes.DENTAL &&
    selectedChipValue === CareCategories.DENTAL &&
    !dentalNetworkId &&
    showDentalChip
  );
};

export const isNonUhcVisionMember = (chipValue) => {
  const visionNetworkId = getNetworkIdForDentalVision(
    getCurrentMember(),
    ReverseVisionCodes
  );
  const { lineOfBusiness } = getCurrentMember();
  const isMNR = isMnrMember(lineOfBusiness);
  const isNonUhcmember =
    chipValue === CareCategories.VISION &&
    visionNetworkId === undefined &&
    isMNR;
  return isNonUhcmember;
};
export const getDomainForAutoComplete = (currentMember) => {
  const memberCoverageType =
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType;
  if (memberCoverageType?.length === 1) {
    const memberCoverageTypeCode =
      memberCoverageType[0]?.typeCode?.coverageTypeCode;

    return memberCoverageTypeCode === 'OBH' || memberCoverageTypeCode === 'B'
      ? 'behavioral'
      : undefined;
  }

  return undefined;
};

export const getMemberHasDesignatedNetwork = (coverage, network: string) =>
  !!coverage?.designatedNetwork?.includes(network);

export const getNetworkIdForDentalVision = (currentMember, codes) => {
  const networks =
    currentMember?.networks?.filter((coverage) =>
      codes?.includes(ReverseCoverageTypesCodes[coverage?.type])
    ) || [];
  return networks[0]?.value[0];
};

export const isNonMember = (callingUser: string | null | undefined): boolean =>
  callingUser != null && callingUser.toLowerCase() !== 'member';

export const getMarketType = (
  selectedMember,
  coverageTypeCode: string | null | undefined = null
) =>
  selectedMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage) =>
      coverage.typeCode?.coverageTypeCode === (coverageTypeCode ?? 'M')
  )?.marketType;

export const getProviderType = (providerType?: string): string => {
  const type = providerType?.toLowerCase();

  if (type === APIConstants.PROVIDER_TYPES.practitioner.toLowerCase())
    return 'PROVIDER';
  if (
    type === APIConstants.PROVIDER_TYPES.providerGroup.toLowerCase() ||
    type === 'medical group'
  )
    return 'GROUP';
  return 'FACILITY';
};

export const parseLOB = (lob) =>
  LINEOFBUSINESS_TRANSLATION[`${lob}`] || LineOfBusiness.EI;

export const extractCoverageWithNetwork = (memberInfo, featureFlags) => {
  if (!memberInfo) {
    return [{ coverageType: null, reciprocityId: null }];
  }
  const { eligibility } = memberInfo;

  if (!eligibility || eligibility.length === 0) {
    return [{ coverageType: null, reciprocityId: null }];
  }

  const coverageData = eligibility[0]?.memberHealthCoverage?.coverageType || [];
  if (!coverageData || coverageData.length === 0) {
    return [{ coverageType: null, reciprocityId: null }];
  }

  const { isPreEffective = false } = memberInfo;

  const coverageWithNetwork = coverageData.map((item) => ({
    coverageType: item.typeCode.coverageTypeCode || null,
    cesProductCode: item.productCode || null,
    reciprocityId:
      getNetworkIdsForPES(
        memberInfo,
        item.typeCode.coverageTypeCode,
        featureFlags
      ) || null,
    policyID: item.policyNumber || null,
    planYear:
      getMemberPlanYear(
        isPreEffective,
        getCoverageEffDateRange(memberInfo, item.typeCode.coverageTypeCode)
          .startDate
      ) || null,
    planVariationCode: item.planVariationCode || null,
  }));
  return coverageWithNetwork;
};

export const getCoverageReciprocityIdMap = (memberInfo, featureFlags) => {
  const coverageWithNetwork = extractCoverageWithNetwork(
    memberInfo,
    featureFlags
  );

  return coverageWithNetwork.reduce((acc, item) => {
    if (item.reciprocityId) {
      acc[item.coverageType] = item.reciprocityId;
    }
    return acc;
  }, {});
};

// Helper function to pad policyId to 7 digits
export const padPolicyId = (id: string | number): string =>
  id?.toString().padStart(7, '0');

export const getAoeFiltersFromLagoon = (
  aoeFiltersLagoon: any[] = [],
  providerType = '',
  enableAgesTreatedFilterFlag = false
) => {
  if (providerType?.toLowerCase() !== ProviderType.PRACTITIONER.toLowerCase()) {
    return [];
  }

  return (
    aoeFiltersLagoon
      // Filter out Ages Treated options if the feature flag is disabled
      .filter(
        ({ dntGroupName }) =>
          !(!enableAgesTreatedFilterFlag && dntGroupName === 'Ages Treated')
      )
      .map(({ aoeCodes, displayOrder, dntGroupName, dntName, name }) => ({
        aoeCodes,
        displayOrder,
        dntGroupName,
        dntName,
        name,
      }))
  );
};

export const getEnterpriseId = (memberInfo): string =>
  memberInfo?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.[0].coverageID?.find(
    (cov) => cov?.sourceSysCode === 'ENTERPRISE ID'
  )?.id;

export const useIsEligibleForStandaloneDental = (): boolean => {
  const currentMember = useCurrentMember();
  const [enableStandaloneDental] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_STANDALONE_DENTAL,
  ]);

  const coverageTypes = getCoverageTypes(currentMember) || [];

  // If the feature flag is disabled, return false regardless of coverage types
  if (!enableStandaloneDental) {
    return false;
  }

  const hasDentalCoverage = coverageTypes.some((type) =>
    ReverseDentalCodes.includes(type)
  );

  const hasOnlyDentalOrVision = coverageTypes.every(
    (type) =>
      ReverseDentalCodes.includes(type) || ReverseVisionCodes.includes(type)
  );

  return hasDentalCoverage && hasOnlyDentalOrVision;
};
